import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { IClub, IAddOn } from "@eagerdog/interfaces";
import { Config } from "@eagerdog/config";
import { httpService } from "src/services/http.service";
import { helperService } from "src/services/helper.service";
import { toast } from "src/components/Toast/ToastManager";
import Button from "src/components/Button/Button";
import { Modal, useModal } from "src/components/Modal/Modal";
import StoreItem from "./StoreItem";
import CreateStoreItem from "./CreateStoreItem";
import EditStoreItem from "./EditStoreItem";

import styles from "./StoreTab.module.scss";

export interface ICartItem extends IAddOn {
  quantity: number;
}

interface IProps {
  club: IClub;
  isAdmin: boolean;
}

const StoreTab: React.FC<IProps> = ({ club, isAdmin }) => {
  const navigate = useNavigate();
  const [items, setItems] = useState<IAddOn[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [cart, setCart] = useState<Map<string, number>>(new Map());
  const [selectedItem, setSelectedItem] = useState<IAddOn | null>(null);
  const [itemToDelete, setItemToDelete] = useState<IAddOn | null>(null);
  const { isShown, toggle } = useModal();
  const { isShown: isEditShown, toggle: toggleEdit } = useModal();
  const { isShown: isDeleteShown, toggle: toggleDelete } = useModal();
  
  const currency = club.address?.country === "USA" ? "USD" : "CAD";

  const loadStoreItems = useCallback(async () => {
    try {
      setLoading(true);
      const response = await httpService.get(
        `${Config.web.REACT_APP_API_URL}/api/clubs/${club._id}/store/items`
      );
      setItems(response.data);
    } catch (error) {
      console.error("Failed to load store items:", error);
    } finally {
      setLoading(false);
    }
  }, [club._id]);

  useEffect(() => {
    loadStoreItems();
    
    const storedCart = localStorage.getItem(`club_store_cart_${club._id}`);
    if (storedCart) {
      const cartItems: ICartItem[] = JSON.parse(storedCart);
      const cartMap = new Map<string, number>();
      cartItems.forEach(item => {
        // Only add to cart if item has a valid _id
        if (item._id) {
          cartMap.set(item._id, item.quantity);
        }
      });
      setCart(cartMap);
    }
  }, [club._id, loadStoreItems]);

  const handleAddToCart = (itemId: string, quantity: number) => {
    const newCart = new Map(cart);
    
    if (quantity <= 0) {
      newCart.delete(itemId);
    } else {
      newCart.set(itemId, quantity);
    }
    
    setCart(newCart);
    
    const cartItems: ICartItem[] = Array.from(newCart.entries())
      .map(([id, qty]) => {
        const item = items.find(i => i._id === id);
        if (!item) return null; // Skip items that no longer exist
        return { ...item, quantity: qty };
      })
      .filter((item): item is ICartItem => item !== null); // Remove null entries
    
    localStorage.setItem(`club_store_cart_${club._id}`, JSON.stringify(cartItems));
  };

  const handleCheckout = () => {
    // For non-admin users, check if they're logged in
    if (!isAdmin) {
      const user = helperService.getUser();
      if (!user._id || !helperService.isLoggedIn()) {
        toast.show({
          title: "Login Required",
          content: "Please log in or register to complete your purchase",
          duration: 5000,
          type: "fail"
        });
        
        navigate(`/login?r=/k/${club._id}/store/checkout`);
        return;
      }
    }
    
    navigate(`/k/${club._id}/store/checkout`, { state: { isManager: isAdmin } });
  };

  const getTotalItems = (): number => {
    return Array.from(cart.values()).reduce((sum, qty) => sum + qty, 0);
  };

  const handleCreateFinish = () => {
    toggle();
    loadStoreItems();
  };

  const handleEdit = (item: IAddOn) => {
    setSelectedItem(item);
    toggleEdit();
  };

  const handleEditFinish = () => {
    toggleEdit();
    setSelectedItem(null);
    loadStoreItems();
  };

  const handleDelete = (item: IAddOn) => {
    setItemToDelete(item);
    toggleDelete();
  };

  const confirmDelete = async () => {
    if (!itemToDelete) return;

    try {
      await httpService.put(
        `${Config.web.REACT_APP_API_URL}/api/clubs/${club._id}/store/items/${itemToDelete._id}`,
        { ...itemToDelete, is_active: false }
      );
      toast.show({
        title: "Success",
        content: "Store item deleted successfully!",
        type: "success",
        duration: 5000
      });
      toggleDelete();
      setItemToDelete(null);
      loadStoreItems();
    } catch (error: any) {
      console.error("Error deleting store item:", error);
      toast.show({
        title: "Error",
        content: error.response?.data?.message || "Failed to delete store item",
        type: "fail",
        duration: 5000
      });
    }
  };

  if (loading) {
    return (
      <div className={styles.loading}>
        <span className="fa fa-spinner fa-spin" />
        <p>Loading store items...</p>
      </div>
    );
  }

  const cartItems = Array.from(cart.entries())
    .map(([id, quantity]) => {
      const item = items.find(i => i._id === id);
      return item ? { ...item, quantity } : null;
    })
    .filter(Boolean) as (IAddOn & { quantity: number })[];

  const getCartTotal = (): number => {
    return cartItems.reduce((sum, item) => sum + (item.fee * item.quantity), 0);
  };

  return (
    <div className={styles.storeTab}>
      <div className={styles.storeHeader}>
        <div className={styles.headerLeft}>
          <h2>{club.club_name} Store</h2>
          {items.length > 0 && (
            <p className={styles.subtitle}>
              {items.length} {items.length === 1 ? "item" : "items"} available
            </p>
          )}
        </div>
        {isAdmin && (
          <div className={styles.adminActions}>
            <Button 
              onClick={() => navigate(`/k/${club._id}/manage/store-transactions`)} 
              className={styles.viewTransactionsButton}
            >
              View Transactions
            </Button>
            <Button onClick={toggle} className={styles.addButton}>
              Add Item
            </Button>
          </div>
        )}
      </div>

      {items.length === 0 ? (
        <div className={styles.emptyState}>
          <h3>No Items Available</h3>
          <p>
            {isAdmin
              ? "Get started by adding your first item to the store."
              : "Check back soon for new items!"}
          </p>
        </div>
      ) : (
        <div className={styles.storeContent}>
          <div className={styles.storeGrid}>
            {items.map((item) => (
              <StoreItem
                key={item._id}
                item={item}
                cartQuantity={cart.get(item._id) || 0}
                onAddToCart={handleAddToCart}
                isAdmin={isAdmin}
                currency={currency}
                onEdit={handleEdit}
                onDelete={handleDelete}
              />
            ))}
          </div>

          <div className={styles.cartSidebar}>
            <div className={styles.cartHeader}>
              <h3>Shopping Cart</h3>
              <span className={styles.itemCount}>
                {getTotalItems()} {getTotalItems() === 1 ? "item" : "items"}
              </span>
            </div>

            {cartItems.length === 0 ? (
              <div className={styles.emptyCart}>
                <p>Your cart is empty</p>
              </div>
            ) : (
              <>
                <div className={styles.cartItems}>
                  {cartItems.map((item) => (
                    <div key={item._id} className={styles.cartItem}>
                      <div className={styles.cartItemInfo}>
                        <h4>{item.title}</h4>
                        <p className={styles.cartItemPrice}>${item.fee.toFixed(2)} {currency} each</p>
                      </div>
                      <div className={styles.cartItemControls}>
                        <div className={styles.quantityControls}>
                          <button onClick={() => handleAddToCart(item._id, item.quantity - 1)}>
                            −
                          </button>
                          <span>{item.quantity}</span>
                          <button onClick={() => handleAddToCart(item._id, item.quantity + 1)}>
                            +
                          </button>
                        </div>
                        <p className={styles.cartItemTotal}>
                          ${(item.fee * item.quantity).toFixed(2)} {currency}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>

                <div className={styles.cartFooter}>
                  <div className={styles.cartTotal}>
                    <span>Subtotal:</span>
                    <strong>${getCartTotal().toFixed(2)} {currency}</strong>
                  </div>
                  <Button onClick={handleCheckout} className={styles.checkoutButton}>
                    Proceed to Checkout
                  </Button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      <Modal 
        isShown={isShown} 
        hide={toggle}
        className="createStoreItemModal"
        modalContent={
          <CreateStoreItem
            clubId={club._id}
            onFinish={handleCreateFinish}
            onCancel={toggle}
          />
        }
      />

      <Modal 
        isShown={isEditShown} 
        hide={toggleEdit}
        className="editStoreItemModal"
        modalContent={
          selectedItem ? (
            <EditStoreItem
              item={selectedItem}
              clubId={club._id}
              onFinish={handleEditFinish}
              onCancel={() => {
                toggleEdit();
                setSelectedItem(null);
              }}
            />
          ) : <div />
        }
      />

      <Modal 
        isShown={isDeleteShown} 
        hide={toggleDelete}
        className="deleteConfirmModal"
        modalContent={
          <div className="deleteConfirmModal">
            <h3>Delete Store Item</h3>
            <p>Are you sure you want to delete "{itemToDelete?.title}"? This action cannot be undone.</p>
            <div className="modalActions">
              <Button 
                variant="secondary"
                onClick={() => {
                  toggleDelete();
                  setItemToDelete(null);
                }}
              >
                Cancel
              </Button>
              <Button 
                variant="default"
                onClick={confirmDelete}
              >
                Delete
              </Button>
            </div>
          </div>
        }
      />
    </div>
  );
};

export default StoreTab;

